import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Table, Modal, Button, Form, Container, Row, Col, Badge, Spinner, Alert } from 'react-bootstrap';
import { FaDatabase } from 'react-icons/fa';
import './Documents.css';

const STORE_API_URL = process.env.REACT_APP_API_URL;

function DocumentsAdmin() {
  const { token } = useContext(AuthContext);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [documentCRTs, setDocumentCRTs] = useState([]);
  const [loadingCRTs, setLoadingCRTs] = useState(false);
  
  // Delete states (hold-to-delete functionality)
  const [deletingId, setDeletingId] = useState(null);
  const [deletePressTimer, setDeletePressTimer] = useState(null);
  
  const navigate = useNavigate();

  useEffect(() => {
    if (token) {
      fetchDocuments();
    }
  }, [token]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (deletePressTimer) {
        clearTimeout(deletePressTimer);
      }
    };
  }, [deletePressTimer]);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${STORE_API_URL}/documents`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to fetch documents');
      const docs = await response.json();
      setDocuments(docs);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (document) => {
    setEditingDocument({ ...document });
    setShowEditModal(true);
    fetchDocumentCRTs(document._id);
  };

  const fetchDocumentCRTs = async (documentId) => {
    setLoadingCRTs(true);
    try {
      const response = await fetch(`${STORE_API_URL}/documents/${documentId}/crts`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to fetch CRTs');
      const crts = await response.json();
      setDocumentCRTs(crts);
    } catch (error) {
      console.error('Error fetching document CRTs:', error);
      toast.error('Failed to load CRTs using this document');
      setDocumentCRTs([]);
    } finally {
      setLoadingCRTs(false);
    }
  };

  const handleDetachFromCRT = async (crtId, crtTitle) => {
    if (!window.confirm(`Are you sure you want to remove "${editingDocument.display_name}" from "${crtTitle}"?`)) {
      return;
    }

    try {
      const response = await fetch(`${STORE_API_URL}/crts/${crtId}/detach-document`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ document_id: editingDocument._id })
      });

      if (!response.ok) throw new Error('Failed to detach document');
      
      toast.success(`Document removed from "${crtTitle}"`);
      
      // Refresh the CRTs list and documents list
      fetchDocumentCRTs(editingDocument._id);
      fetchDocuments();
      
    } catch (error) {
      console.error('Error detaching document:', error);
      toast.error('Failed to remove document from CRT');
    }
  };

  const handleSave = async () => {
    if (!editingDocument || !editingDocument.display_name || !editingDocument.type) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const response = await fetch(`${STORE_API_URL}/documents/${editingDocument._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          display_name: editingDocument.display_name,
          type: editingDocument.type,
          description: editingDocument.description || ''
        })
      });

      if (!response.ok) throw new Error('Failed to update document');
      
      toast.success('Document updated successfully');
      setShowEditModal(false);
      setEditingDocument(null);
      setDocumentCRTs([]);
      fetchDocuments();
    } catch (error) {
      console.error('Error updating document:', error);
      toast.error('Failed to update document');
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!token) return;

    try {
      console.log(`Force deleting document: ${documentId}`);
      
      const response = await fetch(`${STORE_API_URL}/documents/${documentId}/force-delete`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to delete document');
      }

      console.log('Delete result:', result);
      
      // Show detailed success message
      let message = `Document "${result.details?.document_name || 'Unknown'}" deleted successfully`;
      if (result.details?.crts_updated > 0) {
        message += ` and removed from ${result.details.crts_updated} CRT(s)`;
      }
      if (result.details?.file_deleted) {
        message += ` (file deleted from storage)`;
      }
      
      toast.success(message);
      fetchDocuments(); // Refresh the list
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error(error.message || 'Failed to delete document');
    }
  };



  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Container>
      <ToastContainer />
      <div className="headline d-flex justify-content-between align-items-center">
        <h2 className="underline">Document Library</h2>
      </div>
      
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <Button className="btn btn-primary me-2 mb-2" onClick={() => navigate('/superadmin/crts')}>
            <FaDatabase style={{ marginRight: '5px' }} />CRT Database
          </Button>
        </div>
      </div>

      <Alert variant="info" className="mb-4">
        <strong>Shared Document Library</strong>
        <br />
        View and manage all documents in the shared library. You can edit document metadata using the Edit button.
        Documents are uploaded when editing individual CRTs.
      </Alert>
      
      {loading ? (
        <div className="text-center p-4">
          <Spinner animation="border" />
          <p className="mt-2">Loading documents...</p>
        </div>
      ) : (
        <>
          {documents.length === 0 ? (
            <Alert variant="info">
              No documents found in the shared library. Upload documents when editing CRTs to get started.
            </Alert>
          ) : (
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Size</th>
                  <th>Usage</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {documents.map(doc => (
                  <tr key={doc._id}>
                    <td>
                      <div>
                        <strong>{doc.display_name}</strong>
                        <br />
                        <small className="text-muted">{doc.original_filename}</small>
                        {doc.description && (
                          <>
                            <br />
                            <small className="text-muted">{doc.description}</small>
                          </>
                        )}
                      </div>
                    </td>
                    <td><Badge bg="primary">{doc.type}</Badge></td>
                    <td>{formatFileSize(doc.file_size)}</td>
                    <td>
                      {doc.usage_count > 0 ? (
                        <Badge bg="success">{doc.usage_count} CRT(s)</Badge>
                      ) : (
                        <Badge bg="light" text="dark">Unused</Badge>
                      )}
                    </td>
                    <td>{formatDate(doc.created_at)}</td>
                    <td>
                      <div className="d-flex gap-2" style={{ whiteSpace: 'nowrap' }}>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => window.open(doc.url, '_blank')}
                        >
                          Download
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => handleEdit(doc)}
                        >
                          Edit
                        </Button>
                        <div style={{ position: 'relative', display: 'inline-block' }}>
                          <Button
                            variant="outline-warning"
                            size="sm"
                            onMouseDown={() => {
                              setDeletingId(doc._id);
                              const timer = setTimeout(() => {
                                handleDeleteDocument(doc._id);
                                setDeletingId(null);
                              }, 3000);
                              setDeletePressTimer(timer);
                            }}
                            onMouseUp={() => {
                              clearTimeout(deletePressTimer);
                              setDeletingId(null);
                            }}
                            onMouseLeave={() => {
                              clearTimeout(deletePressTimer);
                              setDeletingId(null);
                            }}
                            title="Hold for 3 seconds to delete document and remove from all CRTs"
                          >
                            Delete
                          </Button>
                          {deletingId === doc._id && (
                            <div className="hold-progress-bar" />
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </>
      )}

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => {
        setShowEditModal(false);
        setEditingDocument(null);
        setDocumentCRTs([]);
      }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Document</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingDocument && (
            <Form>
              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>Display Name *</Form.Label>
                    <Form.Control
                      type="text"
                      value={editingDocument.display_name || ''}
                      onChange={(e) => setEditingDocument({
                        ...editingDocument,
                        display_name: e.target.value
                      })}
                      placeholder="How this document should be displayed"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Type *</Form.Label>
                    <Form.Select
                      value={editingDocument.type || ''}
                      onChange={(e) => setEditingDocument({
                        ...editingDocument,
                        type: e.target.value
                      })}
                    >
                      <option value="">Select Type</option>
                      <option value="Service Manual">Service Manual</option>
                      <option value="User Manual">User Manual</option>
                      <option value="Training Document">Training Document</option>
                      <option value="Technical Specs">Technical Specs</option>
                      <option value="Schematics">Schematics</option>
                      <option value="Datasheet">Datasheet</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={editingDocument.description || ''}
                      onChange={(e) => setEditingDocument({
                        ...editingDocument,
                        description: e.target.value
                      })}
                      placeholder="Optional description..."
                    />
                  </Form.Group>
                </Col>
              </Row>
              <div className="text-muted mb-3">
                <small>
                  <strong>Original filename:</strong> {editingDocument.original_filename}<br />
                  <strong>File size:</strong> {formatFileSize(editingDocument.file_size)}<br />
                  <strong>Usage:</strong> {editingDocument.usage_count} CRT(s)
                </small>
              </div>

              {/* CRTs using this document */}
              <div className="mb-3">
                <h6>CRTs using this document:</h6>
                {loadingCRTs ? (
                  <div className="text-center p-2">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loading CRTs...
                  </div>
                ) : documentCRTs.length === 0 ? (
                  <Alert variant="info" className="mb-0">
                    This document is not currently attached to any CRTs.
                  </Alert>
                ) : (
                  <div className="border rounded p-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {documentCRTs.map((crt) => (
                      <div key={crt._id} className="d-flex justify-content-between align-items-center mb-2 p-2 border-bottom">
                        <div className="flex-grow-1">
                          <strong>{crt.title || 'Untitled CRT'}</strong>
                          <br />
                          <small className="text-muted">
                            {crt.brand && crt.model ? `${crt.brand} ${crt.model}` : crt.slug}
                          </small>
                        </div>
                        <div className="d-flex gap-2">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => window.open(`/crts/${crt.slug}`, '_blank')}
                          >
                            View
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDetachFromCRT(crt._id, crt.title || crt.slug)}
                          >
                            Detach
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowEditModal(false);
            setEditingDocument(null);
            setDocumentCRTs([]);
          }}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>


    </Container>
  );
}

export default DocumentsAdmin; 